import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Grid,
  Paper,
} from "@mui/material";
import {
  User,
  FileText,
  ChevronRight,
  ChevronLeft,
  Receipt,
} from 'lucide-react';
import {
  EntitiesListResponseModel,
  EntitiesListResponseModelShape,
} from "../../utils/models/EntitiesListResponseModel";
import { UserModel } from "../../utils/models/UserModel";
import { getAllInvoicesByUserId } from "../invoices/InvoiceService";
import { USDollar, getDateSting } from "../../utils/functions/Helpers";
import ReceiptForm from "./ReceiptForm";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { InvoiceModel } from "../../utils/models/InvoiceModel";
import Constants from "../../utils/constants/Constants";
import { tokens } from "../../theme";

const steps = ['Host Selection', 'Account Exposure', 'Receipt Authorization'];

const MultiStepsReceiptForm = (props) => {
  const { setState, toggleModal, usersData } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [activeStep, setActiveStep] = useState(0);
  const [user, setUser] = useState(UserModel);
  const [invoicesData, setInvoicesData] = useState(EntitiesListResponseModel);
  const setIsLoading = useLoadingSpinner();
  const [invoice, setInvoice] = useState(InvoiceModel);

  const enableNextButton = () => {
    if (activeStep === 0) return !!user.userId && !!invoice.id;
    return true;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSelectUserChange = (e) => {
    const selectedUser = usersData.content.find(
      (u) => u.userId === e.target.value
    );
    setUser(selectedUser || UserModel);
    setInvoice(InvoiceModel); // Reset invoice when user changes
  };

  const onSelectInvoicesChange = (e) => {
    const selectedInvoice = invoicesData.content.find(
      (r) => r.id === parseInt(e.target.value)
    );
    setInvoice(selectedInvoice || InvoiceModel);
  };

  useEffect(() => {
    if (user.userId) {
      getAllInvoicesByUserId(setInvoicesData, user.userId, setIsLoading);
    }
  }, [user.userId, setIsLoading]);

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <Receipt size={32} color={colors.greenAccent[500]} />
        <Box>
          <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
            Guided Receipting
          </Typography>
          <Typography variant="body1" sx={{ color: colors.grey[300] }}>
            Follow the strategic sequence to authorize the financial receipt.
          </Typography>
        </Box>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  '&.Mui-active': { color: colors.greenAccent[500] },
                  '&.Mui-completed': { color: colors.greenAccent[500] },
                }
              }}
            >
              <Typography fontWeight="600">{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: '300px' }}>
        {activeStep === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                <Typography variant="h6" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <User size={18} /> Host Identification
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="user-select-label">Select Event Host</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={user.userId || ""}
                    onChange={onSelectUserChange}
                    label="Select Event Host"
                    sx={{ borderRadius: "12px" }}
                  >
                    <MenuItem value="">Select Host</MenuItem>
                    {usersData.content.map((u) => (
                      <MenuItem key={u.userId} value={u.userId}>
                        {u.firstName} {u.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                <Typography variant="h6" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileText size={18} /> Pending Invoice
                </Typography>
                {user.userId ? (
                  invoicesData.content && invoicesData.content.length > 0 ? (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="invoice-select-label">Select Unpaid Invoice</InputLabel>
                      <Select
                        labelId="invoice-select-label"
                        value={invoice.id || ""}
                        onChange={onSelectInvoicesChange}
                        label="Select Unpaid Invoice"
                        sx={{ borderRadius: "12px" }}
                      >
                        <MenuItem value="">Select Invoice</MenuItem>
                        {invoicesData.content
                          .filter(
                            (inv) =>
                              inv.status !== Constants.INVOICE_STATUS.PAID &&
                              inv.status !== Constants.INVOICE_STATUS.WITHDRAWN
                          )
                          .map((inv) => (
                            <MenuItem key={inv.id} value={inv.id}>
                              {getDateSting(inv.issuedDate)} — {inv.status} — {USDollar.format(inv.amountDue)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography color="error" fontWeight="600">No unpaid invoices found for this host.</Typography>
                    </Box>
                  )
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color={colors.grey[400]}>Select a host first to see invoices.</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && user.userId && invoice.id && (
          <Box sx={{ mt: -2 }}>
            <ReceiptForm
              invoice={invoice}
              selectedUserId={user.userId}
              selectedInvoiceId={invoice.id}
              selectedReservationId={invoice.reservation?.id ?? invoice.reservationId}
              setState={setState}
              toggleModal={toggleModal}
            />
          </Box>
        )}
      </Box>

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            endIcon={<ChevronRight size={18} />}
            disabled={!enableNextButton()}
            onClick={handleNext}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              bgcolor: colors.greenAccent[500],
              color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
              "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
            }}
          >
            Authorize Receipt
          </Button>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ChevronLeft size={18} />}
            onClick={handleBack}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              borderColor: colors.grey[400],
              color: colors.grey[100],
              "&:hover": { borderColor: colors.greenAccent[500], bgcolor: `${colors.greenAccent[500]}0D` }
            }}
          >
            Refine selection
          </Button>
        </Box>
      )}
    </Box>
  );
};

MultiStepsReceiptForm.propTypes = {
  setState: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  usersData: EntitiesListResponseModelShape.isRequired,
};

export default MultiStepsReceiptForm;
